import type { DownstreamServiceIndicator, DownstreamServiceStatus, DownstreamServiceTier } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ErrorBanner, LoadingState } from '@/components/ui/state-views';
import { Tabs } from '@/components/ui/tabs';
import { getDownstreamHealth } from '@/config/api-helpers';

export const meta = {
  label: 'Third-Party Status',
  order: 25,
};

const TIER_TABS: { id: DownstreamServiceTier; label: string }[] = [
  { id: 'tier1', label: 'Tier 1 · Product-critical' },
  { id: 'tier2', label: 'Tier 2 · Operational/supporting' },
];

const OTHER_STATUS_PAGES = [
  { name: 'AuthZed', url: 'https://status.authzed.com' },
  { name: 'Gemini', url: 'https://aistudio.google.com/status' },
  { name: 'Salesforce', url: 'https://status.salesforce.com' },
  { name: 'Greenhouse', url: 'https://status.greenhouse.io' },
  { name: 'Monday', url: 'https://status.monday.com' },
  { name: 'Snowflake', url: 'https://status.snowflake.com' },
  { name: 'Google Cloud', url: 'https://status.cloud.google.com' },
  { name: 'Meta', url: 'https://metastatus.com' },
  { name: 'Webflow', url: 'https://status.webflow.com' },
];

const INDICATOR_DOT: Record<DownstreamServiceIndicator, string> = {
  none: 'bg-green-500',
  minor: 'bg-yellow-400',
  major: 'bg-orange-500',
  critical: 'bg-red-500',
  unknown: 'bg-neutral-400',
};

const INDICATOR_LABEL: Record<DownstreamServiceIndicator, string> = {
  none: 'Operational',
  minor: 'Degraded',
  major: 'Partial Outage',
  critical: 'Major Outage',
  unknown: 'Unknown',
};

function ServiceCard({ svc }: { svc: DownstreamServiceStatus }) {
  const content = (
    <>
      <div className='flex items-center gap-2'>
        <span className={`size-2.5 shrink-0 rounded-full ${INDICATOR_DOT[svc.indicator]}`} />
        <span className='text-sm font-medium text-neutral-900'>{svc.name}</span>
      </div>
      <p className='text-xs text-neutral-500'>{svc.description}</p>
      <p className='text-xs text-neutral-400'>{svc.error ?? INDICATOR_LABEL[svc.indicator]}</p>
    </>
  );

  const className =
    'flex flex-col gap-2 rounded-lg border border-neutral-200 bg-white p-4 transition-shadow hover:shadow-sm';

  return (
    <a href={svc.statusPageUrl} target='_blank' rel='noreferrer' className={className}>
      {content}
    </a>
  );
}

function SecondsSince({ since }: { since: number }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    setElapsed(Math.floor((Date.now() - since) / 1000));
    const id = window.setInterval(() => setElapsed(Math.floor((Date.now() - since) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [since]);

  return <span>{elapsed}s ago</span>;
}

export function DownstreamHealthPage() {
  const [tier, setTier] = useState<DownstreamServiceTier>('tier1');
  const { data, error, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['downstream-health'],
    queryFn: getDownstreamHealth,
    refetchInterval: 120_000,
    staleTime: 90_000,
  });

  const visibleServices = data?.services.filter((svc) => svc.tier === tier);

  return (
    <div className='space-y-4 p-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-lg font-semibold text-neutral-900'>Third-Party Status</h1>
          <p className='text-sm text-neutral-500'>
            Vendor status refreshes automatically about every 2 minutes.
            {data?.checkedAt != null && (
              <>
                {' '}
                Updated <SecondsSince since={Date.parse(data.checkedAt)} />.
              </>
            )}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className='rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50'
        >
          Refresh
        </button>
      </div>

      {isLoading && data == null && <LoadingState message='Loading third-party status…' />}

      {error && (
        <ErrorBanner
          message={error instanceof Error ? error.message : 'Failed to load third-party status'}
          onRetry={() => refetch()}
        />
      )}

      {visibleServices != null && (
        <>
          <Tabs tabs={TIER_TABS} activeTab={tier} onTabChange={(id) => setTier(id as DownstreamServiceTier)} />
          <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'>
            {visibleServices.map((svc) => (
              <ServiceCard key={svc.name} svc={svc} />
            ))}
          </div>
        </>
      )}

      <section className='border-t border-neutral-200 pt-4'>
        <h2 className='text-sm font-medium text-neutral-900'>Other vendor status pages</h2>
        <p className='text-xs text-neutral-500'>
          Links only. These providers are not included in the live checks above.
        </p>
        <div className='mt-2 flex flex-wrap gap-x-4 gap-y-2'>
          {OTHER_STATUS_PAGES.map((statusPage) => (
            <a
              key={statusPage.name}
              href={statusPage.url}
              target='_blank'
              rel='noreferrer'
              className='text-sm text-blue-600 hover:underline'
            >
              {statusPage.name}
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
